// [S-285] Precedent detail — the "open file" view for a precedent.
//
// [S-286] When a source document is stored (the original upload, or a
// generated PDF for seeded precedents) the header shows an "Open source file"
// button that streams the bytes from GET /precedents/:id/source and opens them
// inline. Otherwise we surface the extracted clauses + deal terms and say so.
// Reached from a search result or the browse list at /precedent/:id
// (optionally ?tenantId=… for platform_admin scope).
//
// Token usage only — no hardcoded colors.
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components";
import { SourceViewer } from "../components/SourceViewer";
import { ApiError, useApiBlob } from "../data/api";
import { useGetPrecedent, type PrecedentDetail as Detail } from "../data/precedentsQuery";
import {
  PrecedentVisibilityBadge,
  PrecedentVisibilityControl,
} from "./PrecedentVisibilityControl";

// [S-413] Optional props let the Studio Agents drawer embed this view by id
// (no route nav). With no props it reads the route as before. `embedded` hides
// the page-level back link, which is meaningless inside the drawer.
export function PrecedentDetail({
  id: idProp,
  tenantId: tenantIdProp,
  embedded = false,
}: {
  id?: string;
  tenantId?: string;
  embedded?: boolean;
} = {}): JSX.Element {
  const params = useParams();
  const [search] = useSearchParams();
  const id = idProp ?? params["id"];
  const tenantId = tenantIdProp ?? search.get("tenantId") ?? undefined;
  const q = useGetPrecedent(id, tenantId);

  return (
    <div className="precd">
      {!embedded && (
        <div className="precd__back">
          <Link to="/precedent" className="precd__back__link">
            ← Back to precedents
          </Link>
        </div>
      )}

      {q.isLoading && (
        <div className="prec__state" role="status">
          Opening precedent…
        </div>
      )}

      {q.isError && <DetailError error={q.error} />}

      {q.data !== undefined && (
        <DetailBody detail={q.data} tenantId={tenantId} />
      )}
    </div>
  );
}

// [S-286] Fetch the source bytes (auth headers carried by useApiBlob) and open
// the type-aware in-app SourceViewer (PDF / DOCX / text / download fallback).
function useSourceViewer(
  id: string,
  tenantId: string | undefined
): {
  openViewer: () => void;
  opening: boolean;
  error: string | null;
  blob: Blob | null;
  viewerOpen: boolean;
  setViewerOpen: (open: boolean) => void;
} {
  const fetchBlob = useApiBlob("admin");
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  function openViewer(): void {
    setError(null);
    setOpening(true);
    const qs = tenantId !== undefined ? `?tenantId=${encodeURIComponent(tenantId)}` : "";
    fetchBlob(`/precedents/${encodeURIComponent(id)}/source${qs}`)
      .then(({ blob: b }) => {
        setBlob(b);
        setViewerOpen(true);
      })
      .catch((e: unknown) => {
        const msg =
          e instanceof ApiError && e.status === 404
            ? "No source file is stored for this precedent."
            : e instanceof Error
              ? e.message
              : "Couldn't open the source file.";
        setError(msg);
      })
      .finally(() => setOpening(false));
  }

  return { openViewer, opening, error, blob, viewerOpen, setViewerOpen };
}

function DetailBody({
  detail,
  tenantId,
}: {
  detail: Detail;
  tenantId: string | undefined;
}): JSX.Element {
  const source = useSourceViewer(detail.documentId, tenantId);
  const parties = detail.parties ?? [];
  const [primaryParty, ...counterparties] = parties;
  const counterparty = counterparties.join(", ");
  const { baseAmount, currency } = detail.compensation;
  const clauseCount = detail.clauses.length;
  const dimensions = Object.entries(detail.dimensions ?? {});

  return (
    <>
      {/* HERO — the precedent's identity card: who, what kind, how big. */}
      <header className="precd-hero" data-testid="precedent-detail">
        <div className="precd-hero__top">
          <div className="precd-hero__lead">
            <div className="precd-hero__eyebrow">
              <span className="src-dot stud" aria-hidden="true" />
              Precedent
              <span className="precd-hero__sep" aria-hidden="true">
                ·
              </span>
              <span className="precd-hero__date">
                indexed {formatDate(detail.uploadedAt)}
              </span>
            </div>
            <h1 className="precd-hero__title">
              {primaryParty !== undefined ? (
                <>
                  {primaryParty}
                  {counterparty.length > 0 && (
                    <>
                      <span className="precd-hero__vs" aria-hidden="true">
                        ⇄
                      </span>
                      {counterparty}
                    </>
                  )}
                </>
              ) : (
                precedentTitle(detail)
              )}
            </h1>
            <div className="precd-hero__chips">
              {detail.dimensions?.["instrumentType"] !== undefined && (
                <span className="chip chip--instrument">
                  {prettyValue(detail.dimensions["instrumentType"])}
                </span>
              )}
              {detail.dealType !== null && (
                <span className="chip">{prettyValue(detail.dealType)}</span>
              )}
              {detail.guild !== null && (
                <span className="chip">{detail.guild}</span>
              )}
              {detail.tier !== null && (
                <span className="chip">{prettyValue(detail.tier)}</span>
              )}
              {/* [S-277] Visibility tier badge (non-default tiers only). */}
              <PrecedentVisibilityBadge visibility={detail.visibility} />
            </div>
          </div>

          {baseAmount != null && (
            <div className="precd-hero__value">
              <div className="precd-hero__amt">
                {formatMoney(baseAmount, currency)}
              </div>
              <div className="precd-hero__amtlbl">
                Deal value · {clauseCount} clause{clauseCount === 1 ? "" : "s"}
              </div>
            </div>
          )}
        </div>

        <div className="precd-hero__meta">
          <span className="precd-hero__state">
            <span className="dot" aria-hidden="true" />
            {prettyValue(detail.lifecycleState)}
          </span>
          {dimensions.map(([k, v]) => (
            <span key={k} className="precd-hero__metaitem">
              <span className="precd-hero__metadot" aria-hidden="true">
                •
              </span>
              <span
                className="precd-hero__dim"
                data-testid="precedent-dimension"
                title={prettyValue(k)}
              >
                {prettyValue(k)}: {prettyValue(v)}
              </span>
            </span>
          ))}
        </div>

        <div className="precd-hero__actions">
          {detail.hasSource && (
            <Button
              variant="primary"
              onClick={source.openViewer}
              disabled={source.opening}
              data-testid="precedent-open-source"
            >
              {source.opening ? "Opening…" : "Open source file ↗"}
            </Button>
          )}
          {/* [S-277] Exclude / re-share — owner or tenant_admin only. */}
          <PrecedentVisibilityControl
            documentId={detail.documentId}
            visibility={detail.visibility}
            ownerUid={detail.ownerUid}
            tenantId={tenantId}
          />
          {source.error !== null && (
            <p className="precd__source__error" role="alert">
              {source.error}
            </p>
          )}
        </div>
      </header>

      <SourceViewer
        open={source.viewerOpen}
        onOpenChange={source.setViewerOpen}
        blob={source.blob}
        baseName={
          primaryParty !== undefined
            ? [primaryParty, ...counterparties].join(" vs ")
            : precedentTitle(detail)
        }
      />

      {parties.length > 0 && (
        <section aria-label="Parties">
          <div className="precd-sec__head">
            <h2 className="precd-sec__title">Parties</h2>
            <span className="precd-sec__hint">
              extracted from the executed agreement
            </span>
          </div>
          <ul className="precd__parties__list">
            {parties.map((p) => (
              <li key={p} className="precd__party" data-testid="precedent-party">
                {p}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* [Phase 2] The legacy contract-level "Compensation" section was removed:
          economics now live on the clauses (each clause's type-specific
          `attributes`, e.g. a License Fee clause → {amount, currency, schedule}),
          correctly labeled per instrument instead of mislabeling a license fee
          as "Compensation". `compensationTerms` is retained as data (it backs
          the amount-range filter + browse "deal size"). */}

      <section aria-label="Indexed clauses">
        <div className="precd-sec__head">
          <h2 className="precd-sec__title">Indexed content</h2>
          <span className="precd-sec__hint">
            {clauseCount} clause{clauseCount === 1 ? "" : "s"} · extracted text
            {detail.hasSource
              ? " · open the source file above for the full document"
              : " (no source file stored)"}
          </span>
        </div>
        {clauseCount === 0 ? (
          <div className="prec__state">No clauses were extracted.</div>
        ) : (
          <ol className="precd__clause-list">
            {detail.clauses.map((c) => (
              <li key={c.id} className="cl" data-testid="precedent-clause">
                <div className="cl__head">
                  <span className="cl__num" aria-hidden="true" />
                  <span className="cl__type">{prettyValue(c.type)}</span>
                </div>
                <p className="cl__text">{c.text}</p>
                {Object.keys(c.attributes ?? {}).length > 0 && (
                  <dl className="cl__attrs" data-testid="precedent-clause-attrs">
                    {Object.entries(c.attributes).map(([k, v]) => (
                      <div key={k} className="cl__attr">
                        <dt>{prettyValue(k)}</dt>
                        <dd>{v}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  );
}

function DetailError({ error }: { error: Error }): JSX.Element {
  const notFound = error instanceof ApiError && error.status === 404;
  return (
    <div className="prec__state prec__state--cta" role="alert">
      <div className="prec__state__title">
        {notFound ? "Precedent not found" : "Couldn't open precedent"}
      </div>
      <p>
        {notFound
          ? "This precedent no longer exists, or it isn't in the selected tenant."
          : error.message}
      </p>
      <Link to="/precedent">
        <Button variant="bare" className="prec__state__cta">
          Back to precedents →
        </Button>
      </Link>
    </div>
  );
}

function prettyValue(d: string): string {
  return d.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

// [S-285] A precedent has no stored name (the original filename isn't kept), so
// compose a readable title from its deal facets; fall back to a generic label.
// The full id stays available as a short ref in the meta row.
function precedentTitle(d: Detail): string {
  const parts = [
    d.dealType !== null ? prettyValue(d.dealType) : null,
    d.guild,
    d.tier !== null ? prettyValue(d.tier) : null,
  ].filter((x): x is string => x !== null && x !== "");
  return parts.length > 0 ? parts.join(" · ") : "Precedent";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatMoney(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount}`;
  }
}
